import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Col, Row, Spinner, Carousel, Button } from 'react-bootstrap';
import camelCaseRecursive from 'camelcase-keys-recursive';
import { useAbility } from '@casl/react';
import { indexBenefitsRequest } from '../../../requests/benefits';
import BenefitCard from '../../../screens/Shared/Benefit/BenefitCard';
import { LinkBtn } from '../../Utils/Button';
import BenefitSidePanel from '../../../screens/Shared/Benefit/BenefitSidePanel';
import BenefitModalShow from '../../../screens/Shared/Benefit/BenefitModalShow';
import { indexCompanyAnnouncementsRequest } from '../../../requests/companyAnnouncements';
import CompanyAnnouncementCard from '../../../screens/Shared/CompanyAnnouncement/CompanyAnnouncementCard';
import { AbilityContext } from '../../../config/abilityContext';
import ShowButtonBlack from '../../HRDashboardStyled/images/ShowButtonBlack.png';

const Benefits = () => {
  const ability = useAbility(AbilityContext);
  const [benefits, setBenefits] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [activeTab, setActiveTab] = useState('benefits');
  const dispatch = useDispatch();
  const [showSpinner, setShowSpinner] = useState(true);
  const [activeAnnouncementIndex, setActiveAnnouncementIndex] = useState(0);
  const [activeBenefitIndex, setActiveBenefitIndex] = useState(0);
  const [panelShow, setPanelShow] = useState(false);
  const [panelTitle, setPanelTitle] = useState('');
  const [panelContent, setPanelContent] = useState(null);
  const canIndexBenefits = ability.can('read', 'Benefit');
  const canIndexAnnouncements = ability.can('index', 'CompanyAnnouncement');
  const showMoreInfoButton = activeTab === 'benefits' ? canIndexBenefits : canIndexAnnouncements;

  const handleSuccessIndex = response => {
    setBenefits(camelCaseRecursive(response.data.data));
    setShowSpinner(false);
  };

  const handleRequest = () => {
    console.log('Iniciando solicitud de beneficios', canIndexBenefits);
    if (!canIndexBenefits) {
      setShowSpinner(false);
      return;
    }

    indexBenefitsRequest({
      dispatch,
      params: {
        active: moment().format('DD/MM/YYYY'),
        my_job_management: true,
        display_length: 5,
        sort_column: 'id',
        sort_direction: 'desc'
      },
      successCallback: handleSuccessIndex,
      failureCallback: () => setShowSpinner(false)
    });
  };

  const handleAnnouncementsRequest = () => {
    if (!canIndexAnnouncements) {
      setShowSpinner(false);
      return;
    }

    setShowSpinner(true);
    indexCompanyAnnouncementsRequest({
      dispatch,
      params: {
        active: moment().format('DD/MM/YYYY'),
        my_announcements: true
      },
      successCallback: res => {
        const formatted = camelCaseRecursive(res.data.data);
        setAnnouncements(formatted);
        setShowSpinner(false);
      },
      callback: () => setShowSpinner(false)
    });
  };

  const handleShowInfo = benefit => {
    setPanelTitle('Detalle del Beneficio');
    setPanelContent(<BenefitModalShow benefit={benefit} />);
    setPanelShow(true);
  };

  const handleTabClick = tab => {
    setActiveTab(tab);
    if (tab === 'announcements') {
      if (announcements.length === 0) {
        handleAnnouncementsRequest();
      }
    } else if (tab === 'benefits') {
      if (benefits.length === 0) {
        handleRequest();
      }
    }
  };

  const handleIndicatorKeyPress = (event, callback) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  useEffect(handleRequest, []);

  const renderContent = () => {
    if (showSpinner) {
      return (
        <div className="text-center mt-5 mb-5">
          <Spinner animation="border" variant="primary" />
        </div>
      );
    }

    if (activeTab === 'announcements') {
      if (announcements.length === 0) {
        return (
          <div className="text-center mt-5 mb-5">
            <p className="mt-2" style={{ height: 233, textAlign: 'center' }}>
              No hay anuncios disponibles en este momento.
            </p>
          </div>
        );
      }

      return (
        <>
          <Carousel
            indicators={false}
            style={{ height: '90%' }}
            prevIcon={<span style={{ color: '#007bff', fontSize: '30px' }}>‹</span>}
            nextIcon={<span style={{ color: '#007bff', fontSize: '30px' }}>›</span>}
            interval={5000}
            onSelect={index => setActiveAnnouncementIndex(index)}
          >
            {announcements.map(announcement => (
              <Carousel.Item key={`announcement-${announcement.id}`} style={{ height: '100%' }}>
                <Row className="justify-content-center h-100">
                  <Col xs={12} md={10} lg={8} style={{ height: '100%' }}>
                    <CompanyAnnouncementCard location="dashboard" announcement={announcement} />
                  </Col>
                </Row>
              </Carousel.Item>
            ))}
          </Carousel>

          <div className="text-center mt-3">
            {announcements.map(announcement => {
              const index = announcements.findIndex(a => a.id === announcement.id);
              const isActive = index === activeAnnouncementIndex;

              return (
                <button
                  type="button"
                  key={`indicator-${announcement.id}`}
                  style={{
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: isActive ? '#007bff' : '#ddd',
                    margin: '0 5px',
                    cursor: 'pointer',
                    border: 'none',
                    padding: 0
                  }}
                  onClick={() => {
                    if (index !== -1) {
                      setActiveAnnouncementIndex(index);
                    }
                  }}
                  onKeyPress={e =>
                    handleIndicatorKeyPress(e, () => {
                      if (index !== -1) {
                        setActiveAnnouncementIndex(index);
                      }
                    })
                  }
                  aria-label={`Ir al anuncio ${index + 1} de ${announcements.length}`}
                  tabIndex={0}
                />
              );
            })}
          </div>
        </>
      );
    }

    // Tab de beneficios
    if (benefits.length === 0) {
      return (
        <div className="text-center mt-5 mb-5">
          <p className="mt-2" style={{ height: 233, textAlign: 'center' }}>
            No hay beneficios disponibles en este momento.
          </p>
        </div>
      );
    }

    return (
      <>
        <Carousel
          indicators={false}
          style={{ height: '75%' }}
          prevIcon={<span style={{ color: '#007bff', fontSize: '30px' }}>‹</span>}
          nextIcon={<span style={{ color: '#007bff', fontSize: '30px' }}>›</span>}
          interval={5000}
          onSelect={index => setActiveBenefitIndex(index)}
        >
          {benefits.map(benefit => (
            <Carousel.Item key={`benefit-${benefit.id}`} style={{ height: '100%' }}>
              <Row className="justify-content-center h-100">
                <Col xs={12} md={10} lg={8} style={{ height: '100%' }}>
                  <BenefitCard benefits={benefit} location="dashboard" showInfo={() => handleShowInfo(benefit)} />
                </Col>
              </Row>
            </Carousel.Item>
          ))}
        </Carousel>

        <div className="text-center mt-4 mb-2">
          {benefits.map(benefit => {
            const index = benefits.findIndex(b => b.id === benefit.id);
            const isActive = index === activeBenefitIndex;

            return (
              <button
                type="button"
                key={`indicator-${benefit.id}`}
                style={{
                  display: 'inline-block',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: isActive ? '#007bff' : '#ddd',
                  margin: '0 5px',
                  cursor: 'pointer',
                  border: 'none',
                  padding: 0
                }}
                onClick={() => {
                  if (index !== -1) {
                    setActiveBenefitIndex(index);
                  }
                }}
                aria-label={`Ir al beneficio ${index + 1} de ${benefits.length}`}
                tabIndex={0}
              />
            );
          })}
        </div>
      </>
    );
  };

  return (
    <>
      <div
        className="mt-1"
        id="HRDashboard"
        style={{
          backgroundColor: '#fff',
          border: '1px solid #E0E0E0',
          borderRadius: '8px',
          padding: '15px',
          height: '97%'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            marginBottom: '15px',
            borderBottom: '2px solid #f0f0f0',
            paddingBottom: '10px',
            gap: '10px'
          }}
        >
          <Button
            variant={activeTab === 'announcements' ? 'primary' : 'outline-primary'}
            onClick={() => handleTabClick('announcements')}
            style={{
              borderRadius: '20px',
              padding: '5px 20px',
              fontWeight: 'bold'
            }}
          >
            Anuncios
          </Button>

          <Button
            variant={activeTab === 'benefits' ? 'primary' : 'outline-primary'}
            onClick={() => handleTabClick('benefits')}
            style={{
              borderRadius: '20px',
              padding: '5px 20px',
              fontWeight: 'bold'
            }}
          >
            Beneficios
          </Button>

          {showMoreInfoButton && (
            <LinkBtn
              variant="link"
              to={activeTab === 'announcements' ? '/profile/company_announcements' : '/profile/benefits'}
              style={{
                marginLeft: 'auto',
                padding: '5px 20px',
                fontWeight: 'bold',
                borderRadius: '20px'
              }}
            >
              <img src={ShowButtonBlack} alt="Ver perfil" />
            </LinkBtn>
          )}
        </div>

        <div style={{ height: '90%' }}>{renderContent()}</div>
      </div>

      <BenefitSidePanel
        title={panelTitle}
        show={panelShow}
        onHide={() => {
          setPanelShow(false);
          setPanelContent(null);
        }}
      >
        {panelContent}
      </BenefitSidePanel>
    </>
  );
};

export default Benefits;
